import React, { useEffect, useState } from 'react';
import { getUsers, getUserStats, getPartenaires, createUser, updateUser, toggleUserStatus, resetUserPassword,
  getUserAffectations, setUserAffectations, getSecteurs, getLocalites, generateAgentLocationLink } from '../services/api';

interface UserForm {
  email: string;
  nom: string;
  prenom: string;
  telephone: string;
  role: string;
  partenaire_id: string;
  password: string;
}

const emptyForm: UserForm = { email: '', nom: '', prenom: '', telephone: '', role: 'partenaire', partenaire_id: '', password: '' };

const Users: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [partenaires, setPartenaires] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<any>({});
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<any>(null);
  const [form, setForm] = useState<UserForm>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  // Affectations
  const [affModal, setAffModal] = useState<{ open: boolean; user: any | null }>({ open: false, user: null });
  const [allSecteurs, setAllSecteurs] = useState<any[]>([]);
  const [allLocalites, setAllLocalites] = useState<any[]>([]);
  const [selSecteurIds, setSelSecteurIds] = useState<Set<string>>(new Set());
  const [selLocaliteIds, setSelLocaliteIds] = useState<Set<string>>(new Set());
  const [affLoading, setAffLoading] = useState(false);
  const [affSaving, setAffSaving] = useState(false);
  const [affSearch, setAffSearch] = useState('');

  // Tracking link modal
  const [trackModal, setTrackModal] = useState<{ open: boolean; user: any; link: string }>({ open: false, user: null, link: '' });
  const [trackLoading, setTrackLoading] = useState(false);

  useEffect(() => { getPartenaires().then(r => setPartenaires(r.data)).catch(() => {}); }, []);

  const loadStats = () => {
    getUserStats().then(r => setStats(r.data)).catch(() => {});
  };

  const loadUsers = () => {
    const params: Record<string, string> = { page: page.toString(), limit: '30' };
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v as string; });
    getUsers(params).then(r => {
      setUsers(r.data.data || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.totalPages || 1);
    }).catch(() => {});
  };

  useEffect(() => { loadStats(); }, []);
  useEffect(() => { loadUsers(); }, [page, filters]);

  const updateFilter = (key: string, value: string) => {
    setFilters((prev: any) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  /** Ouvre la modale d'affectation pour un user : charge ses affectations actuelles
   *  + tous les secteurs/localites de son partenaire. */
  const openAffectationsModal = async (user: any) => {
    if (!user.partenaire_id) {
      alert('Cet utilisateur n\'est rattache a aucun partenaire.');
      return;
    }
    setAffModal({ open: true, user });
    setAffLoading(true);
    setAffSearch('');
    try {
      const [secResp, locResp, affResp] = await Promise.all([
        getSecteurs({ partenaire_id: user.partenaire_id }),
        getLocalites({ partenaire_id: user.partenaire_id }),
        // Affectations : si la route n'existe pas encore (backend non redemarre),
        // on intercepte pour ne pas casser le chargement du reste.
        getUserAffectations(user.id).catch(() => ({ data: [] })),
      ]);
      setAllSecteurs(Array.isArray(secResp.data) ? secResp.data : []);
      setAllLocalites(Array.isArray(locResp.data) ? locResp.data : []);
      const sIds = new Set<string>();
      const lIds = new Set<string>();
      const affList = Array.isArray(affResp.data) ? affResp.data : [];
      affList.forEach((a: any) => {
        if (a.secteur_id) sIds.add(a.secteur_id);
        if (a.localite_id) lIds.add(a.localite_id);
      });
      setSelSecteurIds(sIds);
      setSelLocaliteIds(lIds);
    } catch (e) {
      const err = e as { message?: string };
      alert('Erreur de chargement : ' + (err.message || 'inconnue'));
      setAffModal({ open: false, user: null });
    } finally {
      setAffLoading(false);
    }
  };

  const toggleSecteur = (id: string) => {
    setSelSecteurIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /** Toggle localite : si elle n'est pas pleinement cochee, on coche TOUS ses secteurs
   *  (et la localite). Sinon on decoche la localite + tous ses secteurs. */
  const toggleLocalite = (id: string) => {
    const childSecteurs = allSecteurs.filter(s => s.localite_id === id);
    const childIds = childSecteurs.map(s => s.id);
    const allChildrenChecked = childIds.length > 0 && childIds.every(sid => selSecteurIds.has(sid));
    const localiteWasChecked = selLocaliteIds.has(id);
    const fullyChecked = allChildrenChecked && localiteWasChecked;

    setSelLocaliteIds(prev => {
      const next = new Set(prev);
      if (fullyChecked) next.delete(id); else next.add(id);
      return next;
    });
    setSelSecteurIds(prev => {
      const next = new Set(prev);
      if (fullyChecked) {
        childIds.forEach(sid => next.delete(sid));
      } else {
        childIds.forEach(sid => next.add(sid));
      }
      return next;
    });
  };

  const saveAffectations = async () => {
    if (!affModal.user) return;
    setAffSaving(true);
    try {
      await setUserAffectations(affModal.user.id, {
        secteur_ids: Array.from(selSecteurIds),
        localite_ids: Array.from(selLocaliteIds),
      });
      setAffModal({ open: false, user: null });
    } catch (e) {
      const err = e as { response?: { data?: { error?: string } }; message?: string };
      alert('Erreur : ' + (err.response?.data?.error || err.message || 'inconnue'));
    } finally {
      setAffSaving(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setForm(emptyForm);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const openEditModal = (user: any) => {
    setEditingUser(user);
    setForm({
      email: user.email || '',
      nom: user.nom || '',
      prenom: user.prenom || '',
      telephone: user.telephone || '',
      role: user.role || 'partenaire',
      partenaire_id: user.partenaire_id ? String(user.partenaire_id) : '',
      password: ''
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.email || !form.nom || !form.role) {
      setError('Email, nom et role sont obligatoires');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingUser) {
        await updateUser(editingUser.id, {
          ...form,
          old_partenaire_id: editingUser.partenaire_id || ''
        });
        setSuccess('Utilisateur modifie avec succes');
      } else {
        await createUser(form);
        setSuccess('Utilisateur cree avec succes');
      }
      loadUsers();
      loadStats();
      setTimeout(() => { setShowModal(false); setSuccess(''); }, 1200);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleStatus = async (user: any) => {
    try {
      await toggleUserStatus(user.id, { partenaire_id: user.partenaire_id || '' });
      loadUsers();
      loadStats();
    } catch {
      alert('Erreur lors du changement de statut');
    }
  };

  const handleResetPassword = async (user: any) => {
    if (!window.confirm(`Reinitialiser le mot de passe de ${user.prenom} ${user.nom} ?`)) return;
    try {
      await resetUserPassword(user.id, { partenaire_id: user.partenaire_id || '' });
      alert('Mot de passe reinitialise (Forgest@2024)');
    } catch {
      alert('Erreur lors de la reinitialisation');
    }
  };

  const openTrackingLink = async (user: any) => {
    setTrackLoading(true);
    setTrackModal({ open: true, user, link: '' });
    try {
      const r = await generateAgentLocationLink(user.id);
      const url = `${window.location.origin}/track/${r.data.token}`;
      setTrackModal({ open: true, user, link: url });
    } catch (e: any) {
      alert(e.response?.data?.error || 'Erreur generation lien');
      setTrackModal({ open: false, user: null, link: '' });
    } finally {
      setTrackLoading(false);
    }
  };

  const copyTrackingLink = async () => {
    try {
      await navigator.clipboard.writeText(trackModal.link);
      alert('Lien copie dans le presse-papier');
    } catch {
      const el = document.getElementById('track-link-input') as HTMLInputElement | null;
      if (el) { el.select(); document.execCommand('copy'); alert('Lien copie'); }
    }
  };

  const openInWhatsapp = () => {
    const u = trackModal.user;
    if (!u?.telephone) { alert('Cet utilisateur n\'a pas de telephone enregistre'); return; }
    const phone = String(u.telephone).replace(/\D/g, '').replace(/^0+/, '');
    const withCountry = phone.startsWith('221') ? phone : '221' + phone;
    const msg = `Bonjour ${u.prenom || ''}, cliquez sur ce lien pour partager votre position : ${trackModal.link}`;
    window.open(`https://wa.me/${withCountry}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, { bg: string; color: string; label: string }> = {
      superadmin: { bg: '#fce4ec', color: '#c62828', label: 'Super Admin' },
      supersuperadmin: { bg: '#f3e5f5', color: '#6a1b9a', label: 'Super Admin' },
      admin: { bg: '#e3f2fd', color: '#1565c0', label: 'Admin' },
      partenaire: { bg: '#e8f5e9', color: '#2e7d32', label: 'Partenaire' },
      releveur: { bg: '#fff3e0', color: '#e65100', label: 'Releveur' }
    };
    const s = styles[role] || { bg: '#f5f5f5', color: '#666', label: role || '-' };
    return (
      <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>
        {s.label}
      </span>
    );
  };

  const getStatusBadge = (etat: number) => (
    <span style={{
      padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
      background: etat === 1 ? '#e8f5e9' : '#ffebee',
      color: etat === 1 ? '#2e7d32' : '#c62828'
    }}>
      {etat === 1 ? 'Actif' : 'Inactif'}
    </span>
  );

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #ddd',
    fontSize: 14, boxSizing: 'border-box'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 4
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ color: '#333', margin: 0 }}>Utilisateurs ({total.toLocaleString('fr-FR')})</h2>
        <button
          onClick={openCreateModal}
          style={{
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: '#1976d2', color: '#fff', cursor: 'pointer',
            fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6
          }}
        >
          + Nouvel utilisateur
        </button>
      </div>

      {/* Stats Cards */}
      <div className="dashboard-cards">
        <div className="dash-card bg-blue">
          <span className="dash-card-icon">&#128101;</span>
          <h4>Total Utilisateurs</h4>
          <div className="dash-card-value">{stats?.total || 0}</div>
        </div>
        <div className="dash-card" style={{ background: 'linear-gradient(135deg, #c62828 0%, #e53935 100%)' }}>
          <span className="dash-card-icon">&#128081;</span>
          <h4>Super Admins</h4>
          <div className="dash-card-value">{stats?.superadmins || 0}</div>
        </div>
        <div className="dash-card bg-green">
          <span className="dash-card-icon">&#127760;</span>
          <h4>Partenaires</h4>
          <div className="dash-card-value">{stats?.partenaires || 0}</div>
        </div>
        <div className="dash-card bg-teal">
          <span className="dash-card-icon">&#9989;</span>
          <h4>Actifs</h4>
          <div className="dash-card-value">{stats?.actifs || 0}</div>
        </div>
        <div className="dash-card bg-orange">
          <span className="dash-card-icon">&#10060;</span>
          <h4>Inactifs</h4>
          <div className="dash-card-value">{stats?.inactifs || 0}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Rechercher (nom, email, tel)..."
          value={filters.search || ''}
          onChange={e => updateFilter('search', e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #ddd', fontSize: 14, minWidth: 220 }}
        />
        <select value={filters.role || ''} onChange={e => updateFilter('role', e.target.value)}>
          <option value="">Tous les roles</option>
          <option value="superadmin">Super Admin</option>
          <option value="partenaire">Partenaire</option>
          <option value="releveur">Releveur</option>
        </select>
        <select value={filters.partenaire_id || ''} onChange={e => updateFilter('partenaire_id', e.target.value)}>
          <option value="">Tous les partenaires</option>
          {partenaires.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
        </select>
        <select value={filters.etat || ''} onChange={e => updateFilter('etat', e.target.value)}>
          <option value="">Tous les statuts</option>
          <option value="1">Actif</option>
          <option value="0">Inactif</option>
        </select>
        {Object.values(filters).some(v => v) && (
          <button
            onClick={() => { setFilters({}); setPage(1); }}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#ef5350', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            Reinitialiser
          </button>
        )}
      </div>

      {/* Table */}
      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Telephone</th>
              <th>Role</th>
              <th>Partenaire</th>
              <th>Statut</th>
              <th>Date creation</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((u: any, i: number) => (
              <tr key={u.email || i}>
                <td><strong>{u.prenom} {u.nom}</strong></td>
                <td style={{ fontSize: 13 }}>{u.email}</td>
                <td>{u.telephone || '-'}</td>
                <td>{getRoleBadge(u.role)}</td>
                <td>{u.partenaire || '-'}</td>
                <td>{getStatusBadge(u.etat)}</td>
                <td>{u.created ? new Date(u.created).toLocaleDateString('fr-FR') : '-'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button
                      onClick={() => openEditModal(u)}
                      title="Modifier"
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: '1px solid #1976d2',
                        background: '#e3f2fd', color: '#1976d2', cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }}
                    >
                      &#9998;
                    </button>
                    <button
                      onClick={() => handleToggleStatus(u)}
                      title={u.etat === 1 ? 'Desactiver' : 'Activer'}
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: 'none',
                        background: u.etat === 1 ? '#ffebee' : '#e8f5e9',
                        color: u.etat === 1 ? '#c62828' : '#2e7d32',
                        cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }}
                    >
                      {u.etat === 1 ? '\u2716' : '\u2714'}
                    </button>
                    <button
                      onClick={() => handleResetPassword(u)}
                      title="Reinitialiser mot de passe"
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: '1px solid #ff9800',
                        background: '#fff3e0', color: '#e65100', cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }}
                    >
                      &#128274;
                    </button>
                    <button
                      onClick={() => openAffectationsModal(u)}
                      title="Affecter secteurs / localites"
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: '1px solid #6f42c1',
                        background: '#f3e8ff', color: '#6f42c1', cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }}
                    >
                      &#128205;
                    </button>
                    <button
                      onClick={() => openTrackingLink(u)}
                      title="Envoyer un lien de localisation"
                      style={{
                        padding: '5px 10px', borderRadius: 6, border: '1px solid #0288d1',
                        background: '#e1f5fe', color: '#0277bd', cursor: 'pointer', fontSize: 12, fontWeight: 600
                      }}
                    >
                      &#128225;
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={8} style={{ textAlign: 'center', padding: 40, color: '#999' }}>Aucun utilisateur trouve</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="pagination">
        <button disabled={page <= 1} onClick={() => setPage(page - 1)}>Precedent</button>
        <span style={{ padding: '8px 14px', fontSize: 14 }}>Page {page} / {totalPages}</span>
        <button disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Suivant</button>
      </div>

      {/* Modal Create/Edit */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000
        }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#fff', borderRadius: 16, padding: 30, width: '90%', maxWidth: 520,
              maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 20px', color: '#333' }}>
              {editingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}
            </h3>

            {error && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#ffebee', color: '#c62828', fontSize: 13, marginBottom: 15 }}>
                {error}
              </div>
            )}
            {success && (
              <div style={{ padding: '10px 14px', borderRadius: 8, background: '#e8f5e9', color: '#2e7d32', fontSize: 13, marginBottom: 15 }}>
                {success}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15 }}>
              <div>
                <label style={labelStyle}>Prenom</label>
                <input style={inputStyle} value={form.prenom} onChange={e => setForm({ ...form, prenom: e.target.value })} placeholder="Prenom" />
              </div>
              <div>
                <label style={labelStyle}>Nom *</label>
                <input style={inputStyle} value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} placeholder="Nom" />
              </div>
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Email *</label>
                <input style={inputStyle} type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="email@exemple.com" />
              </div>
              <div>
                <label style={labelStyle}>Telephone</label>
                <input style={inputStyle} value={form.telephone} onChange={e => setForm({ ...form, telephone: e.target.value })} placeholder="+221 7X XXX XX XX" />
              </div>
              <div>
                <label style={labelStyle}>Role *</label>
                <select style={inputStyle} value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
                  <option value="partenaire">Partenaire</option>
                  <option value="releveur">Releveur</option>
                  <option value="superadmin">Super Admin</option>
                </select>
              </div>
              {(form.role === 'partenaire' || form.role === 'releveur') && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Partenaire associe</label>
                  <select style={inputStyle} value={form.partenaire_id} onChange={e => setForm({ ...form, partenaire_id: e.target.value })}>
                    <option value="">-- Selectionner un partenaire --</option>
                    {partenaires.map(p => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                  </select>
                </div>
              )}
              {!editingUser && (
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={labelStyle}>Mot de passe (defaut: Forgest@2024)</label>
                  <input style={inputStyle} type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} placeholder="Laisser vide pour le mot de passe par defaut" />
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 24 }}>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px', borderRadius: 8, border: '1px solid #ddd',
                  background: '#f5f5f5', color: '#666', cursor: 'pointer', fontSize: 14, fontWeight: 600
                }}
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                style={{
                  padding: '10px 24px', borderRadius: 8, border: 'none',
                  background: saving ? '#90caf9' : '#1976d2', color: '#fff',
                  cursor: saving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600
                }}
              >
                {saving ? 'Enregistrement...' : editingUser ? 'Modifier' : 'Creer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== Modale d'affectation Secteurs/Localites ===== */}
      {affModal.open && (
        <div
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
          }}
          onClick={() => !affSaving && setAffModal({ open: false, user: null })}
        >
          <div
            style={{
              background: '#fff', borderRadius: 12, padding: 24,
              width: '92%', maxWidth: 700, maxHeight: '88vh', overflow: 'auto',
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h3 style={{ margin: 0 }}>
                Affectations · {affModal.user?.prenom} {affModal.user?.nom}
              </h3>
              <button
                onClick={() => !affSaving && setAffModal({ open: false, user: null })}
                style={{ background: 'none', border: 'none', fontSize: 24, cursor: 'pointer', color: '#888', lineHeight: 1 }}
              >
                ×
              </button>
            </div>
            <p style={{ color: '#666', fontSize: 12, marginTop: 0 }}>
              Cochez les localites et/ou secteurs auxquels cet agent aura acces.
              Les abonnes synchronises sur son mobile seront limites a ces zones.
              {(!selSecteurIds.size && !selLocaliteIds.size) && (
                <span style={{ color: '#e65100' }}> &nbsp;Si vide : acces a TOUTES les zones du partenaire.</span>
              )}
            </p>

            {affLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Chargement...</div>
            ) : (
              <>
                <input
                  type="search"
                  placeholder="Rechercher une localite ou un secteur..."
                  value={affSearch}
                  onChange={e => setAffSearch(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1px solid #ddd', fontSize: 13, marginBottom: 14, boxSizing: 'border-box',
                  }}
                />

                <div style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  fontSize: 13, fontWeight: 700, color: '#1565c0', marginBottom: 8,
                }}>
                  <span>📍 Localites &amp; secteurs ({selSecteurIds.size}/{allSecteurs.length} secteurs)</span>
                  <button
                    onClick={() => {
                      const allChecked = selSecteurIds.size === allSecteurs.length;
                      if (allChecked) {
                        setSelSecteurIds(new Set());
                        setSelLocaliteIds(new Set());
                      } else {
                        setSelSecteurIds(new Set(allSecteurs.map(s => s.id)));
                        setSelLocaliteIds(new Set(allLocalites.map(l => l.id)));
                      }
                    }}
                    style={{ background: 'none', border: 'none', fontSize: 11, color: '#1976d2', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {selSecteurIds.size === allSecteurs.length ? 'Tout decocher' : 'Tout cocher'}
                  </button>
                </div>

                <div style={{ border: '1px solid #e0e0e0', borderRadius: 8, maxHeight: 420, overflowY: 'auto' }}>
                  {allLocalites
                    .filter(l => {
                      if (!affSearch) return true;
                      const q = affSearch.toLowerCase();
                      if (l.libelle?.toLowerCase().includes(q)) return true;
                      // Garde aussi les localites dont au moins un secteur matche
                      return allSecteurs.some(s => s.localite_id === l.id && s.libelle?.toLowerCase().includes(q));
                    })
                    .map(l => {
                      const localiteChildren = allSecteurs.filter(s => s.localite_id === l.id);
                      const filteredChildren = affSearch
                        ? localiteChildren.filter(s =>
                            s.libelle?.toLowerCase().includes(affSearch.toLowerCase()) ||
                            l.libelle?.toLowerCase().includes(affSearch.toLowerCase())
                          )
                        : localiteChildren;
                      const checkedChildren = localiteChildren.filter(s => selSecteurIds.has(s.id)).length;
                      const fullyChecked = checkedChildren > 0 && checkedChildren === localiteChildren.length;
                      const partiallyChecked = checkedChildren > 0 && !fullyChecked;
                      return (
                        <div key={l.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                          {/* Localite : checkbox parent */}
                          <label
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '10px 12px', cursor: 'pointer', fontSize: 13, fontWeight: 700,
                              background: fullyChecked ? '#ecfdf5' : partiallyChecked ? '#f0fdfa' : '#fafafa',
                              color: '#1f2937',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={fullyChecked}
                              ref={el => { if (el) el.indeterminate = partiallyChecked; }}
                              onChange={() => toggleLocalite(l.id)}
                            />
                            <span style={{ flex: 1 }}>
                              {l.libelle}
                              <span style={{ fontWeight: 400, color: '#6b7280', fontSize: 11, marginLeft: 6 }}>
                                ({checkedChildren}/{localiteChildren.length} secteurs)
                              </span>
                            </span>
                          </label>
                          {/* Secteurs enfants */}
                          {localiteChildren.length === 0 && (
                            <div style={{ padding: '6px 36px', fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>
                              Aucun secteur dans cette localite
                            </div>
                          )}
                          {filteredChildren.map(s => (
                            <label
                              key={s.id}
                              style={{
                                display: 'flex', alignItems: 'center', gap: 8,
                                padding: '6px 12px 6px 36px', cursor: 'pointer', fontSize: 12,
                                background: selSecteurIds.has(s.id) ? '#eff6ff' : 'transparent',
                                color: '#374151', borderTop: '1px solid #f9fafb',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={selSecteurIds.has(s.id)}
                                onChange={() => toggleSecteur(s.id)}
                              />
                              {s.libelle}
                              {s.forage && (
                                <span style={{ fontSize: 10, color: '#9ca3af', marginLeft: 'auto' }}>
                                  ⛲ {s.forage}
                                </span>
                              )}
                            </label>
                          ))}
                        </div>
                      );
                    })}
                  {/* Secteurs orphelins (sans localite) */}
                  {(() => {
                    const orphans = allSecteurs.filter(s => !s.localite_id || !allLocalites.find(l => l.id === s.localite_id));
                    const filteredOrphans = affSearch
                      ? orphans.filter(s => s.libelle?.toLowerCase().includes(affSearch.toLowerCase()))
                      : orphans;
                    if (orphans.length === 0) return null;
                    return (
                      <div style={{ borderTop: '2px solid #e5e7eb' }}>
                        <div style={{
                          padding: '8px 12px', fontSize: 12, fontWeight: 700,
                          color: '#6b7280', background: '#f9fafb',
                        }}>
                          ⚠ Secteurs sans localite ({orphans.length})
                        </div>
                        {filteredOrphans.map(s => (
                          <label
                            key={s.id}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8,
                              padding: '6px 12px 6px 36px', cursor: 'pointer', fontSize: 12,
                              background: selSecteurIds.has(s.id) ? '#eff6ff' : 'transparent',
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selSecteurIds.has(s.id)}
                              onChange={() => toggleSecteur(s.id)}
                            />
                            {s.libelle}
                          </label>
                        ))}
                      </div>
                    );
                  })()}
                  {allLocalites.length === 0 && allSecteurs.length === 0 && (
                    <div style={{ padding: 30, color: '#999', fontSize: 12, textAlign: 'center' }}>
                      Aucune localite ni secteur pour ce partenaire
                    </div>
                  )}
                </div>
              </>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 }}>
              <button
                onClick={() => setAffModal({ open: false, user: null })}
                disabled={affSaving}
                style={{
                  padding: '10px 22px', borderRadius: 8, border: '1px solid #ddd',
                  background: '#fff', color: '#666', cursor: 'pointer', fontSize: 14,
                }}
              >
                Annuler
              </button>
              <button
                onClick={saveAffectations}
                disabled={affSaving || affLoading}
                style={{
                  padding: '10px 22px', borderRadius: 8, border: 'none',
                  background: affSaving ? '#90caf9' : '#1565c0', color: '#fff',
                  cursor: affSaving ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
                }}
              >
                {affSaving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tracking-link modal — issues a signed URL the agent opens on his phone
          to broadcast his position. */}
      {trackModal.open && (
        <div
          onClick={() => setTrackModal({ open: false, user: null, link: '' })}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 12, padding: 22, width: '100%', maxWidth: 520,
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h3 style={{ margin: 0, color: '#0277bd', fontSize: 18 }}>
              Lien de localisation
            </h3>
            <p style={{ margin: '6px 0 16px', fontSize: 13, color: '#666' }}>
              Envoyez ce lien a <strong>{trackModal.user?.prenom} {trackModal.user?.nom}</strong>.
              En ouvrant le lien sur son telephone, l'agent peut partager sa position en un clic.
            </p>

            {trackLoading ? (
              <div style={{ textAlign: 'center', padding: 30, color: '#999' }}>Generation du lien...</div>
            ) : (
              <>
                <input
                  id="track-link-input"
                  type="text"
                  value={trackModal.link}
                  readOnly
                  onFocus={e => e.target.select()}
                  style={{
                    width: '100%', padding: '10px 12px', border: '1.5px solid #0288d1',
                    borderRadius: 8, fontSize: 12, fontFamily: 'monospace',
                    background: '#f0f9ff', color: '#0277bd', marginBottom: 14,
                  }}
                />

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={copyTrackingLink}
                    style={{
                      flex: 1, padding: '10px 14px', border: 'none', borderRadius: 8,
                      background: '#0288d1', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                    }}
                  >Copier le lien</button>
                  {trackModal.user?.telephone && (
                    <button
                      onClick={openInWhatsapp}
                      style={{
                        flex: 1, padding: '10px 14px', border: 'none', borderRadius: 8,
                        background: '#25D366', color: '#fff', fontWeight: 700, fontSize: 13, cursor: 'pointer',
                      }}
                    >Envoyer via WhatsApp</button>
                  )}
                </div>

                <div style={{ marginTop: 14, padding: 10, background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 8, fontSize: 12, color: '#E65100' }}>
                  Le lien reste valide <strong>30 jours</strong>. Chaque clic enregistre une nouvelle position.
                  Vous pourrez consulter l'historique sur la page <strong>Positions agents</strong>.
                </div>

                <button
                  onClick={() => setTrackModal({ open: false, user: null, link: '' })}
                  style={{
                    marginTop: 14, width: '100%', padding: '9px 14px', border: '1px solid #ddd', borderRadius: 8,
                    background: '#fff', color: '#666', fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}
                >Fermer</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Users;
